"use client";
import React, { useState, useEffect } from "react";
import { getAvailableSlots } from "../utils/getAvailableSlots";
import { track } from "../lib/track";

interface ScheduleTreatmentProps {
  meetingType: string;                    // e.g., "Home Treatment"
  treatmentTypes: string[];               // Supply your list from parent
  travelBufferMinutes?: number;           // default 60
  treatmentDurationMinutes?: number;      // default 60
  duration?: number;                      // Optional duration prop
}

type FormValues = {
  name: string;
  email: string;
  phone: string;
  address: string;
  treatmentLocation: string; // e.g., "Home Visit", "Clinic", "Workplace"
  treatmentType: string;
  dob: string;
};

const ScheduleTreatment: React.FC<ScheduleTreatmentProps> = ({
  meetingType,
  travelBufferMinutes = 60,
  treatmentDurationMinutes = 60,
  //duration,
}) => {
  // 1h travel + 1h treatment (default)
  const totalBlockMinutes = travelBufferMinutes + treatmentDurationMinutes;

  const [availableBlocks, setAvailableBlocks] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [selectedBlockRaw, setSelectedBlockRaw] = useState<string | null>(null); // raw 2h start label
  const [selectedSlotLabel, setSelectedSlotLabel] = useState<string | null>(null); // "HH:mm - HH:mm" (treatment only)
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const [months, setMonths] = useState<{ label: string; value: string }[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [monthDays, setMonthDays] = useState<string[]>([]);

  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    email: "",
    phone: "",
    address: "",
    treatmentLocation: "",
    treatmentType: "",
    dob: "",
  });

  // Build next 6 months for month selector
  const getNextSixMonths = () => {
    const out: { label: string; value: string }[] = [];
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const label = date.toLocaleString("default", { month: "long", year: "numeric" });
      const value = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
      out.push({ label, value });
    }
    return out;
  };

  //console.log("Duration: ", duration)

  useEffect(() => {
    setMonths(getNextSixMonths());
  }, []);

  // Weekdays only, starting 2 days from today
  const getWeekdaysInMonth = (monthValue: string): string[] => {
    const [year, month] = monthValue.split("-").map(Number);
    const days: string[] = [];
    const date = new Date(year, month - 1, 1);
    const today = new Date();
    today.setDate(today.getDate() + 2);

    while (date.getMonth() === month - 1) {
      if (date >= today && date.getDay() !== 0 && date.getDay() !== 6) {
        days.push(date.toISOString().split("T")[0]);
      }
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Fetch 2-hour blocks; display only the treatment hour (second hour)
  useEffect(() => {
    if (!selectedDay) return;

    const fetchBlocks = async () => {
      setLoadingSlots(true);
      try {
        const blocks = await getAvailableSlots(selectedDay, totalBlockMinutes);
        setAvailableBlocks(blocks || []);
      } catch (e) {
        console.error("Error fetching available blocks:", e);
        setAvailableBlocks([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    setSelectedBlockRaw(null);
    setSelectedSlotLabel(null);
    fetchBlocks();
  }, [selectedDay, totalBlockMinutes]);

  // Helpers
  const parse12hToDate = (dayISO: string, timeLabel: string) => {
    // Example: "9:00 AM"
    const [time, period] = timeLabel.split(/(AM|PM)/i).map((s) => s.trim());
    const [hStr, mStr] = time.split(":");
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr || "0", 10);
    if (/PM/i.test(period) && h !== 12) h += 12;
    if (/AM/i.test(period) && h === 12) h = 0;
    const d = new Date(`${dayISO}T00:00:00`);
    d.setHours(h, m, 0, 0);
    return d;
  };

  const fmtHHMM = (d: Date) =>
    `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;

  // When user chooses a 2h block start, compute the treatment hour label (the second hour)
  const handleBlockSelect = (blockStartLabel: string) => {
    if (!selectedDay) return;
    setSelectedBlockRaw(blockStartLabel);

    const travelStart = parse12hToDate(selectedDay, blockStartLabel);
    const treatmentStart = new Date(travelStart.getTime() + travelBufferMinutes * 60000);
    const treatmentEnd = new Date(treatmentStart.getTime() + treatmentDurationMinutes * 60000);

    const label = `${fmtHHMM(treatmentStart)} - ${fmtHHMM(treatmentEnd)}`;
    setSelectedSlotLabel(label);
  };

  const validate = () => {
    const required: (keyof FormValues)[] = [
      "name",
      "email",
      "phone",
      "address",
      "treatmentLocation",
      "treatmentType",
    ];
    for (const key of required) {
      if (!formValues[key] || String(formValues[key]).trim() === "") {
        alert(`Please complete: ${key.replace(/([A-Z])/g, " $1")}`);
        return false;
      }
    }
    if (!selectedDay || !selectedSlotLabel || !selectedBlockRaw) {
      alert("Please select a date and treatment time.");
      return false;
    }
    if (!formValues.email.includes("@")) {
      alert("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Compute travel + treatment windows
    const travelStart = parse12hToDate(selectedDay!, selectedBlockRaw!);
    const travelEnd = new Date(travelStart.getTime() + travelBufferMinutes * 60000);
    const treatmentStart = new Date(travelEnd.getTime());
    const treatmentEnd = new Date(treatmentStart.getTime() + treatmentDurationMinutes * 60000);

    // Email/notes body (includes both windows)
    const htmlDetails = `
      <h3>Patient & Treatment Details</h3>
      <p><strong>Name:</strong> ${formValues.name}</p>
      <p><strong>Phone:</strong> ${formValues.phone}</p>
      <p><strong>Email:</strong> ${formValues.email}</p>
      <p><strong>Address:</strong> ${formValues.address}</p>
      <p><strong>Treatment Location:</strong> ${formValues.treatmentLocation}</p>
      <p><strong>Treatment Type:</strong> ${formValues.treatmentType}</p>
      <hr />
      <p><strong>Date:</strong> ${new Date(selectedDay!).toLocaleDateString()}</p>
      <p><strong>Travel Window:</strong> ${fmtHHMM(travelStart)} - ${fmtHHMM(travelEnd)}</p>
      <p><strong>Treatment Window:</strong> ${fmtHHMM(treatmentStart)} - ${fmtHHMM(treatmentEnd)}</p>
      <hr />
      <h4>Additional Info</h4>
      <p><strong>Date of Birth:</strong> ${formValues.dob || "N/A"}</p>
    `;

    // Book treatment-only for patients; add a private busy marker for your travel
    const payload = {
      date: selectedDay,
      // Patients see/receive treatment only:
      slot: `${fmtHHMM(treatmentStart)} - ${fmtHHMM(treatmentEnd)}`,
      email: formValues.email,
      name: formValues.name,
      phone: formValues.phone,
      address: formValues.address,
      treatmentLocation: formValues.treatmentLocation,
      treatmentType: formValues.treatmentType,
      message: htmlDetails,
      meetingType,
      meta: {
        travelStart: travelStart.toISOString(),
        travelEnd: travelEnd.toISOString(),
        treatmentStart: treatmentStart.toISOString(),
        treatmentEnd: treatmentEnd.toISOString(),
      },
      // Private busy event for your calendar (have your API create this with no attendees)
      travelEvent: {
        summary: "Travel Buffer",
        start: travelStart.toISOString(),
        end: travelEnd.toISOString(),
        visibility: "private",
      },
    };

    try {
      const response = await fetch("/api/graph/calendar/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response from API:", errorData);
        alert(`Failed to submit: ${errorData.message}`);
        return;
      }

      alert("Thanks! We’ve pencilled in your slot and sent a confirmation.");
      setTimeout(() => {
        window.location.href = "/";
      }, 1200);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <div className="mt-5 text-xl font-bold text-[var(--color-brand)]">
      <h1>Schedule a Treatment</h1>

      {/* BASIC DETAILS */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-base font-normal">
        <input
          name="name"
          placeholder="Full name"
          value={formValues.name}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        />
        <input
          name="email"
          placeholder="Email"
          value={formValues.email}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        />
        <input
          name="phone"
          placeholder="Phone number"
          value={formValues.phone}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        />
        <input
          name="address"
          placeholder="Treatment address"
          value={formValues.address}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        />

        <select
          name="treatmentLocation"
          value={formValues.treatmentLocation}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        >
          <option value="">Select treatment location ▼</option>
          <option>Home Visit</option>
          <option>Clinic</option>
          <option>Workplace</option>
          <option>Other</option>
        </select>

        <select
          name="treatmentType"
          value={formValues.treatmentType}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        >
          <option value="">Select treatment type ▼</option>
          <option>Deep Tissue Massage</option>
          <option>Full Body/Remedial Massage</option>
          <option>Assessement/Rehab Massage</option>
          <option>Kinesio Taping</option>
          <option>Pregnancy Massage</option>
          <option>Reflexology Massage</option>
          <option>Indian Head Massage</option>
        </select>
      </div>

      {/* OPTIONAL HISTORY */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-base font-normal">
        <input
          name="dob"
          placeholder="Date of birth (optional)"
          value={formValues.dob}
          onChange={handleChange}
          className="rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)]"
        />
      </div>

      {/* DATE PICKERS */}
      <div className="my-4">
        <label className="block mb-1 font-medium text-[var(--color-brand)]">Select Month</label>
        <select
          onChange={(e) => {
            const val = e.target.value;
            setSelectedMonth(val);
            const days = getWeekdaysInMonth(val);
            setMonthDays(days);
            setSelectedDay(null);
            setAvailableBlocks([]);
            track(
              "click", // Event name
              {
                label: `Month selected: ${val}`, // Metadata: label
                component: `ScheduleTreatment - ${meetingType}`, // Metadata: component
              },
              {
                event: "click", // Event name for TrackEvent
                label: `Month selected: ${val}`, // Event label
                component: `ScheduleTreatment - ${meetingType}`, // Component name
                meta: {
                  timestamp: Date.now(), // Optional: Add a timestamp for the event
                },
              }
            );
          }}
          value={selectedMonth || ""}
          className="block rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)] text-[var(--color-brand)]"
        >
          <option value="" disabled>
            Select month ▼
          </option>
          {months.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {selectedMonth && (
        <div className="my-4">
          <label className="block mb-1 font-medium text-[var(--color-brand)]">Select Day</label>
          <select
            onChange={(e) => {
              setSelectedDay(e.target.value);
              setAvailableBlocks([]);
              setSelectedBlockRaw(null);
              setSelectedSlotLabel(null);
            }}
            value={selectedDay || ""}
            className="block rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)] text-[var(--color-brand)]"
          >
            <option value="" disabled>
              Select day ▼
            </option>
            {monthDays.map((day) => {
              const formattedDay = new Date(day).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
              return (
                <option key={day} value={day}>
                  {formattedDay}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* TIME SLOTS (shows second hour as Treatment) */}
      {selectedDay && (
        <div className="my-4">
          <label className="block mb-2 font-medium text-[var(--color-brand)]">Select Time</label>
          {loadingSlots ? (
            <div className="py-6 text-center text-gray-500">Loading available slots...</div>
          ) : availableBlocks.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {availableBlocks.map((blockStartLabel) => {
                // Compute treatment hour display (second hour)
                const travelStart = parse12hToDate(selectedDay!, blockStartLabel);
                const treatmentStart = new Date(travelStart.getTime() + travelBufferMinutes * 60000);
                const treatmentEnd = new Date(treatmentStart.getTime() + treatmentDurationMinutes * 60000);

                const treatmentLabel = `${treatmentStart.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;

                return (
                  <button
                    key={blockStartLabel}
                    onClick={() => handleBlockSelect(blockStartLabel)}
                    aria-pressed={selectedBlockRaw === blockStartLabel}
                    className={`p-2 border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-text-brand-highlight)] ${
                      selectedBlockRaw === blockStartLabel
                        ? "bg-[var(--color-text-brand-highlight)] text-white border-[var(--color-text-brand-highlight)]"
                        : "bg-gray-200 hover:bg-[var(--color-text-brand-highlight)] hover:text-white"
                    }`}
                    title={`Travel: ${fmtHHMM(travelStart)}-${fmtHHMM(
                      new Date(travelStart.getTime() + travelBufferMinutes * 60000)
                    )} • Treatment: ${fmtHHMM(treatmentStart)}-${fmtHHMM(treatmentEnd)}`}
                  >
                    <div className="text-sm font-semibold">{treatmentLabel}</div>
                    
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-red-500 mt-4">
              No 2-hour windows available for the selected day. Please choose another day.
            </p>
          )}
        </div>
      )}

      {/* SUBMIT */}
      {selectedSlotLabel && (
        <div className="mt-6">
          <button
            onClick={handleSubmit}
            className="bg-[var(--color-brand)] text-white px-4 py-2 rounded-md border border-transparent hover:bg-white hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] transition-colors duration-300"
          >
            Confirm & Book
          </button>
          <p className="mt-2 text-sm font-normal text-gray-600">
            You’ll receive an email confirmation and calendar invite.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScheduleTreatment;
